<template>
  <TinyMCEEditor
    v-if="editorType === 'tinymce'"
    ref="editorRef"
    v-bind="$attrs"
    :model-value="modelValue"
    :editor-style="editorStyle"
    :toolbar="toolbar"
    :root-block="rootBlock"
    :has-font="hasFont"
    :product-size="productSize"
    :button-size="buttonSize"
    :disable-image="disableImage"
    :is-text="isText"
    :select-all="selectAll"
    @update:model-value="$emit('update:modelValue', $event)"
    @on-font-size-changed="$emit('onFontSizeChanged', $event)"
    @on-change="$emit('on-change', $event)"
    @on-init="$emit('on-init', $event)"
    @on-blur="$emit('onBlur', $event)"
  />

  <TipTapInlineEditor
    v-else-if="editorType === 'tiptap'"
    ref="editorRef"
    :model-value="modelValue"
    :editor-style="editorStyle"
    :config="config"
    :inverse="inverse"
    :toolbar="toolbar as string[]"
    :inline="inline"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script lang="ts" setup>
  import TinyMCEEditor from "./TinyMCEEditor.vue";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useColorsStore } from "@/stores/colors";
  import { useFontStore } from "@/stores/fonts";
  import { TipTapInlineEditor } from "@topol/tiptap-editor";
  import { useI18n } from "vue-i18n";
  import { ref, StyleValue, computed } from "vue";
  import { useTiptapConfigStore } from "@/stores/tiptapConfig";

  interface Props {
    modelValue: string;
    editorStyle: StyleValue | Record<string, any>;
    toolbar?: string | string[];
    rootBlock?: string;
    hasFont?: boolean;
    productSize?: boolean;
    buttonSize?: boolean;
    disableImage?: boolean;
    isText?: boolean;
    selectAll?: boolean;
    inline?: boolean;
  }

  withDefaults(defineProps<Props>(), {
    modelValue: "",
    toolbar: "",
    rootBlock: "",
    hasFont: false,
    productSize: false,
    buttonSize: false,
    disableImage: false,
    isText: false,
    selectAll: false,
  });

  defineEmits([
    "update:modelValue",
    "onFontSizeChanged",
    "on-change",
    "on-init",
    "onBlur",
  ]);

  const generalStore = useGeneralStore();
  const mergeTagsStore = useMergeTagsStore();
  const fontStore = useFontStore();
  const colorsStore = useColorsStore();
  const { t } = useI18n();
  const tiptapConfigStore = useTiptapConfigStore();

  const editorRef = ref();

  const editorType = computed(() => generalStore.textEditor);

  const config = computed(() => {
    let position = undefined;
    const el = document.querySelector("body");
    const controlPanel = document.getElementById("controlPanel");
    const fixPos = generalStore.textEditorToolbarBottomPosition;
    if (fixPos && el) {
      const controlPanelWidth = controlPanel ? controlPanel.offsetWidth : 381;
      const leftLimit = el.classList.contains("right") ? 0 : controlPanelWidth;
      const rightLimit = el.classList.contains("right") ? controlPanelWidth : 0;
      const worksSpaceWidth =
        window.innerWidth -
        (el.classList.contains("right") ? controlPanelWidth : 0);
      position = {
        bottom: "5px",
        top: "auto",
        left:
          fixPos === "left"
            ? `${leftLimit + 5}px`
            : fixPos === "center"
              ? leftLimit + (worksSpaceWidth - leftLimit) / 2 + "px"
              : "auto",
        right: fixPos === "right" ? `${rightLimit + 5}px` : "auto",
      };
    }
    return {
      language: generalStore.language,
      mergeTags: {
        syntax: mergeTagsStore.MTSyntax,
        mergeTags: mergeTagsStore.mergeTags,
        flatMergeTags: mergeTagsStore.flatMergeTags,
        patterns: mergeTagsStore.smartMergeTags.patterns,
        showTagText: tiptapConfigStore.tiptapConfig.showMergeTagText ?? false,
        title: t("editor.personalisations"),
        singularTitle: t("editor.personalisation"),
      },
      fonts: fontStore.fonts,
      colors: colorsStore.colors,
      position: position,
      center: generalStore.textEditorToolbarBottomPosition === "center",
    };
  });

  const inverse = computed(() => {
    const light = tiptapConfigStore.tiptapConfig.light;
    if (light) {
      return generalStore.light === false;
    }
    return generalStore.light !== false;
  });

  const focus = () => {
    if (editorRef.value && editorRef.value.focus) {
      editorRef.value.focus();
    }
  };

  defineExpose({
    focus,
  });
</script>

<style lang="scss">
  @import "../../css/helpers";

  .mce-content-body td {
    font-size: 11px;
    font-family: Helvetica, Arial, sans-serif;
  }

  .tox {
    z-index: 1600 !important;
  }

  .tox-tinymce-aux {
    z-index: 1601 !important;
  }

  .tox-dialog-wrap__backdrop {
    z-index: 1602 !important;
  }

  .tox-dialog {
    z-index: 1603 !important;
  }

  .tox-tinymce-inline {
    transform: translate(var(--tox-offset), var(--toy-offset)) !important;
    opacity: var(--tox-opactity) !important;
    // position: sticky !important;
  }

  .tox .tox-editor-header {
    left: var(--tox-padding) !important;
    opacity: 1 !important;
    visibility: var(--tox-visible) !important;
  }

  .tox-editor-dock-transition {
    opacity: 1 !important;
  }

  /* Change color of the headins in TinyMCE toolbar*/
  .tox-collection__item-label > * {
    color: rgb(60, 60, 60) !important;
  }

  .topol-special-merge-tag {
    cursor: pointer;
    outline: 1px dashed var(--c-structure-1);
    background-color: inherit !important;
    color: inherit !important;
    font-size: inherit !important;
    font-family: inherit !important;
    font-weight: inherit !important;
    font-style: inherit !important;
    text-decoration: inherit !important;
  }

  .topol-special-merge-tag:hover {
    background-color: #907dff23 !important;
  }

  .topol-special-merge-tag:focus {
    outline: none !important;
    border: none !important;
  }
</style>
